import _ from 'lodash';

import NodeLabels from 'app/beta-editor/NodeLabels';
import { actionNoun as getActionNoun } from 'app/service/ServiceUtils';
import { isPathsGroupNode } from 'app/beta-editor/utils/PathUtils';
import {
  hasNeeds,
  isSetupBehaviorComplete,
} from 'app/beta-editor/utils/FlowUtils';
import { isFilter, isWrite } from 'app/service/utils';

import { Behavior } from 'app/beta-editor/pages/types';
import { Flow, FlowNode } from 'app/beta-editor/flowState';
import { ServicesMap } from 'app/service/types';
import isValidAuth from 'app/beta-editor/utils/isValidAuth';
import isValidFields from 'app/beta-editor/utils/isValidFields';
import hasInvalidMappings from 'app/beta-editor/utils/hasInvalidMappings';

const label = 'Customize';

interface FieldsBehavior extends Behavior {
  isApplicable: (props: any) => boolean;
}

const fieldsBehavior: FieldsBehavior = {
  label(props) {
    const { node, services } = props;
    if (hasInvalidMappings(node)) {
      return `${
        isWrite(node.type_of) ? 'Template' : 'Options'
      } may be out of date.`;
    }
    return `${label} ${NodeLabels.fields(node, services)}`;
  },

  menuLabel(props) {
    const { node, services } = props;
    const text = `${label} ${isWrite(node.type_of) ? 'Template' : 'Options'}`;

    const service = _.get(services, [node.selected_api]);
    const actionNoun = getActionNoun(service, node.action, node.type_of);
    const isReadyToSetupFields = isSetupBehaviorComplete(node);
    const actionLabelText = `${label} ${actionNoun}`;
    return isReadyToSetupFields ? actionLabelText : text;
  },

  crumbLabel(props) {
    return fieldsBehavior.label(props);
  },

  data() {
    return;
  },

  status({ node, flow }) {
    if (hasInvalidMappings(node)) {
      return 'alert';
    } else if (isValidFields(node, flow)) {
      return 'success';
    }
    return ''; // not edited
  },

  isApplicable(props) {
    const { node, flow, services } = props;
    const needs = (flow.needs || {})[node.id];
    const hasNotLoadedNeeds = _.isUndefined(needs);
    const hasMissingNeeds = !!(flow.missingNeeds || {})[node.id];

    return (
      Boolean(node.selected_api) &&
      Boolean(node.action) &&
      isValidAuth(node, services) &&
      (hasNotLoadedNeeds || hasNeeds(props) || hasMissingNeeds)
    );
  },

  visible(props: { node: FlowNode; flow: Flow; services: ServicesMap }) {
    const { node } = props;
    // if we're on an action step, we should always show the fields page
    // otherwise (searches or triggers) show the page only if it's applicable
    const isWriteOrNotFilterAndApplicable =
      isWrite(node.type_of) ||
      (fieldsBehavior.isApplicable(props) && !isFilter(node.type_of));

    // visibility criteria pre-simplified editor
    return !isPathsGroupNode(node) && isWriteOrNotFilterAndApplicable;
  },

  isClickable(props) {
    return !fieldsBehavior.isLoading(props);
  },

  isLocked(props) {
    return !fieldsBehavior.isApplicable(props);
  },

  isLoading({ flow }: { flow: Flow }) {
    return !!flow.isLoadingAllNeeds;
  },
};

export default fieldsBehavior;
